import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMin, fmtTime } from "@/lib/format";
import { StatusBadge, SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Clock, Truck } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { field: "vehicle_arrival", label: "Vehicle Arrival", needs: [] },
  { field: "loading_start", label: "Start Loading", needs: ["vehicle_arrival"] },
  { field: "loading_end", label: "Complete Loading", needs: ["loading_start"] },
  { field: "dispatched", label: "Vehicle Dispatched", needs: ["loading_end"] },
];

export default function Outward() {
  const { user } = useAuth();
  const [recs, setRecs] = useState([]);
  const [bills, setBills] = useState([]);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const [form, setForm] = useState({ bill_no: "", vehicle_number: "", driver_name: "" });
  const canCreate = ["admin", "warehouse_manager", "supervisor", "dispatch"].includes(user?.role);

  const load = useCallback(async () => {
    const res = await api.get("/outward");
    setRecs(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    const res = await api.get("/bills");
    setBills(res.data.filter((b) => ["packed", "packing", "picking", "received"].includes(b.status)));
    setOpen(true);
  };

  const create = async () => {
    try {
      await api.post("/outward", { ...form, vehicle_number: form.vehicle_number || null, driver_name: form.driver_name || null });
      toast.success("Dispatch record created");
      setOpen(false);
      setForm({ bill_no: "", vehicle_number: "", driver_name: "" });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const stamp = async (outwardNo, field) => {
    try {
      const res = await api.post(`/outward/${outwardNo}/timestamp`, { field });
      toast.success("Timestamp recorded");
      setManage(res.data);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="outward-page">
      <SectionTitle
        right={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={openCreate} data-testid="new-dispatch-button">
                  <Plus className="w-4 h-4 mr-1" /> New Dispatch
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader><DialogTitle className="font-heading text-xl uppercase">New Dispatch / Shipping</DialogTitle><DialogDescription className="sr-only">Create a dispatch record for a bill</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Bill</Label>
                    <Select value={form.bill_no} onValueChange={(v) => setForm({ ...form, bill_no: v })}>
                      <SelectTrigger data-testid="dispatch-bill-select"><SelectValue placeholder="Select bill" /></SelectTrigger>
                      <SelectContent>
                        {bills.map((b) => <SelectItem key={b.bill_no} value={b.bill_no}>{b.bill_no} — {b.customer} ({b.status})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Vehicle Number</Label>
                    <Input data-testid="dispatch-vehicle-input" value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} />
                  </div>
                  <div>
                    <Label>Driver Name</Label>
                    <Input data-testid="dispatch-driver-input" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} />
                  </div>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={create} data-testid="dispatch-create-submit">Create Dispatch</Button>
                </div>
              </DialogContent>
            </Dialog>
          )
        }
      >
        Outward / Dispatch
      </SectionTitle>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="outward-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Outward No", "Bill", "Customer", "Vehicle", "Arrival", "Wait", "Loading", "Total", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recs.map((r) => (
              <tr key={r.outward_no} className="border-b border-border/50 hover:bg-accent/40 transition-colors duration-150" data-testid={`outward-row-${r.outward_no}`}>
                <td className="px-4 py-3 metric font-bold text-primary whitespace-nowrap">{r.outward_no}</td>
                <td className="px-4 py-3 metric">{r.bill_no}</td>
                <td className="px-4 py-3">{r.customer}</td>
                <td className="px-4 py-3 metric">{r.vehicle_number || "—"}</td>
                <td className="px-4 py-3 metric text-xs">{fmtTime(r.vehicle_arrival)}</td>
                <td className={`px-4 py-3 metric ${(r.vehicle_waiting_minutes ?? 0) > 30 ? "text-signal-red font-bold" : ""}`}>{fmtMin(r.vehicle_waiting_minutes)}</td>
                <td className="px-4 py-3 metric">{fmtMin(r.loading_minutes)}</td>
                <td className="px-4 py-3 metric font-bold">{fmtMin(r.total_dispatch_minutes)}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  {r.status !== "dispatched" && (
                    <Button size="sm" variant="outline" onClick={() => setManage(r)} data-testid={`outward-manage-${r.outward_no}`}>
                      <Clock className="w-3.5 h-3.5 mr-1" /> Update
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {recs.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No dispatch records</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!manage} onOpenChange={() => setManage(null)}>
        <DialogContent className="bg-card border-border" data-testid="outward-manage-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" /> {manage?.outward_no} — {manage?.bill_no}
            </DialogTitle>
            <DialogDescription className="sr-only">Update dispatch timestamps</DialogDescription>
          </DialogHeader>
          {manage && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{manage.customer} · Vehicle <span className="metric">{manage.vehicle_number || "not assigned"}</span></p>
              {STEPS.map((s) => {
                const done = !!manage[s.field];
                const ready = s.needs.every((n) => manage[n]);
                return (
                  <div key={s.field} className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                    <span className="text-sm">{s.label}</span>
                    {done ? (
                      <span className="metric text-sm text-signal-green font-bold">{fmtTime(manage[s.field])}</span>
                    ) : (
                      <Button size="sm" disabled={!ready} onClick={() => stamp(manage.outward_no, s.field)} className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-40" data-testid={`outward-stamp-${s.field}`}>
                        Stamp now
                      </Button>
                    )}
                  </div>
                );
              })}
              <div className="grid grid-cols-3 gap-2 pt-2 text-center">
                <div className="border border-border rounded-md p-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Vehicle wait</p>
                  <p className="metric font-bold">{fmtMin(manage.vehicle_waiting_minutes)}</p>
                </div>
                <div className="border border-border rounded-md p-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Loading</p>
                  <p className="metric font-bold">{fmtMin(manage.loading_minutes)}</p>
                </div>
                <div className="border border-border rounded-md p-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total dispatch</p>
                  <p className="metric font-bold">{fmtMin(manage.total_dispatch_minutes)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
